import {
  SHOPIFY_STORES,
  CART_KEY_PREFIX,
  COOKIE_CONSENT_KEY,
  BLOOD_VISION_KEYS,
  USER_ANALYTICS_STORAGE_KEY,
} from './constants';
import { UserAnalyticsData, DEFAULT_USER_ANALYTICS } from './types';

export const getStorageData = (): UserAnalyticsData | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(USER_ANALYTICS_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as UserAnalyticsData) : null;
  } catch (e) {
    console.error('[UserAnalytics] Error reading localStorage:', e);
    return null;
  }
};

export const setStorageData = (data: UserAnalyticsData): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      USER_ANALYTICS_STORAGE_KEY,
      JSON.stringify(data),
    );
  } catch (e) {
    console.error('[UserAnalytics] Error writing localStorage:', e);
  }
};

// Heuristic: any of these keys means the user was here before this feature shipped.
// Rationale + caveats: docs/user-analytics.md#pre-existing-users
export const checkForExistingSignals = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    for (const store of SHOPIFY_STORES) {
      const cartKey = `${CART_KEY_PREFIX}${store}`;
      if (window.localStorage.getItem(cartKey)) {
        return true;
      }
    }

    if (window.localStorage.getItem(COOKIE_CONSENT_KEY)) {
      return true;
    }

    for (const key of BLOOD_VISION_KEYS) {
      if (window.localStorage.getItem(key)) {
        return true;
      }
    }

    return false;
  } catch (e) {
    console.error('[UserAnalytics] Error checking for existing signals:', e);
    return false;
  }
};

/** First URL segment, or 'homepage' for the root. */
export const getPageSection = (pathname: string): string => {
  if (pathname === '/' || pathname === '') return 'homepage';

  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || 'other';
};

/** First two URL segments joined ('ring/buy'), or null if there is only one. */
export const getPageSubsection = (pathname: string): string | null => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length >= 2) {
    return `${segments[0]}/${segments[1]}`;
  }

  return null;
};

export const pushToDataLayer = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') return;

  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({
      event,
      ...data,
    });
  } catch (e) {
    console.error('[UserAnalytics] Error pushing to dataLayer:', e);
  }
};

export const createInitialData = (
  isPreExisting: boolean,
): UserAnalyticsData => {
  const now = new Date().toISOString();

  return {
    ...DEFAULT_USER_ANALYTICS,
    firstSeen: now,
    preExistingUser: isPreExisting,
    // Head start so a returning visitor's first tracked visit reads as 2.
    // Any threshold compared against these counts is one lower for them.
    totalVisits: isPreExisting ? 1 : 0,
  };
};

export const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';

  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};
